import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import envelopGray from "../../assests/envelopGray.svg";
import clsx from "clsx";
import checkedBlue from "../../assests/checkedBlue.svg";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { User } from "../../providers/AuthProvider";
import { Menu, Portal, Transition } from "@headlessui/react";
import { Fragment, useCallback, useLayoutEffect, useRef, useState } from "react";
import GetUser from "../../utils/getUser";

const MENU_WIDTH = 150;

interface TUserRow {
  row: User;
  tableAction: {
    name: string;
    icon: typeof faEllipsisV;
    actionHandler: (data: User) => void;
  }[];
}

function MenuPositionEffect({
  open,
  onUpdate,
}: {
  open: boolean;
  onUpdate: () => void;
}) {
  useLayoutEffect(() => {
    if (!open) return;
    onUpdate();
    window.addEventListener("scroll", onUpdate, true);
    window.addEventListener("resize", onUpdate);
    return () => {
      window.removeEventListener("scroll", onUpdate, true);
      window.removeEventListener("resize", onUpdate);
    };
  }, [open, onUpdate]);

  return null;
}

function UserActionsMenu({ row, tableAction }: TUserRow) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const updateMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 8,
      left: Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
      minWidth: MENU_WIDTH,
    });
  }, []);

  return (
    <Menu as="div" className="relative inline-block text-right">
      {({ open }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            onClick={updateMenuPosition}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full hover:bg-overlay"
            aria-label="User actions"
          >
            <FontAwesomeIcon icon={faEllipsisV} className="text-light-gray" />
          </Menu.Button>

          <MenuPositionEffect open={open} onUpdate={updateMenuPosition} />

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Portal>
              <Menu.Items
                style={menuStyle}
                className="z-[9999] rounded-xl border border-edge bg-surface py-1 shadow-overlay focus:outline-none"
              >
                {tableAction.map((action, index) => (
                  <Menu.Item key={index}>
                    {({ active }) => (
                      <button
                        type="button"
                        className={clsx(
                          "flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-[Gilroy-Medium] text-light-gray",
                          active && "bg-overlay text-heading-color",
                        )}
                        onClick={() => action.actionHandler(row)}
                      >
                        <FontAwesomeIcon
                          icon={action.icon}
                          className="text-xs"
                        />
                        {action.name}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Portal>
          </Transition>
        </>
      )}
    </Menu>
  );
}

function StatusBadge({ row }: { row: User }) {
  const statusLabel =
    row.status === "invite-sent" ? "Invite sent" : "Verified";

  return (
    <div
      className={clsx(
        "inline-flex h-[26px] max-w-[120px] items-center gap-1.5 rounded-2xl pl-2 pr-3",
        row.status === "invite-sent" ? "bg-overlay" : "bg-active-background",
      )}
    >
      <img
        src={row.status === "invite-sent" ? envelopGray : checkedBlue}
        className={clsx(
          "h-[16px] w-[16px]",
          row.status === "invite-sent" && "!h-3 !w-4",
        )}
        alt=""
      />
      <p
        className={clsx(
          "whitespace-nowrap text-[11px] font-[Gilroy-Medium]",
          row.status === "invite-sent" ? "text-label-muted" : "text-blue",
        )}
      >
        {statusLabel}
      </p>
    </div>
  );
}

export function UserCard({ row, tableAction }: TUserRow) {
  const currentUser = GetUser();

  return (
    <div className="rounded-xl border border-edge bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-[Gilroy-Medium] text-[15px] text-heading-color">
            {row.name}
          </p>
          <p className="mt-0.5 truncate font-[Gilroy-Medium] text-[13px] text-heading-color">
            {row.email}
          </p>
          <p className="truncate font-[Gilroy-Medium] text-[12px] text-light-gray">
            {row.phone || "—"}
          </p>
        </div>
        {currentUser.type !== "agent" ? (
          <UserActionsMenu row={row} tableAction={tableAction} />
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-overlay px-2.5 py-1 text-[11px] font-[Gilroy-Medium] text-heading-color">
          {row.code || "No code"}
        </span>
        <span className="rounded-full bg-overlay px-2.5 py-1 text-[11px] font-[Gilroy-Medium] capitalize text-light-gray">
          {row.type?.replace("-", " ")}
        </span>
        <StatusBadge row={row} />
      </div>
    </div>
  );
}

const UserRow = ({ row, tableAction }: TUserRow) => {
  const currentUser = GetUser();

  return (
    <tr className="border-b border-edge transition-colors hover:bg-overlay">
      <td className="max-w-[160px] px-4 py-4">
        <p className="truncate font-[Gilroy-Medium] text-[14px] text-heading-color">
          {row.name}
        </p>
      </td>

      <td className="max-w-[120px] px-4 py-4">
        <p className="truncate font-[Gilroy-Medium] text-[13px] text-light-gray">
          {row.code}
        </p>
      </td>

      <td className="max-w-[220px] px-4 py-4">
        <p className="truncate font-[Gilroy-Medium] text-[13px] text-heading-color">
          {row.email}
        </p>
        <p className="truncate font-[Gilroy-Medium] text-[12px] text-light-gray">
          {row.phone || "—"}
        </p>
      </td>

      <td className="px-4 py-4">
        <p className="font-[Gilroy-Medium] text-[13px] capitalize text-light-gray">
          {row.type?.replace("-", " ")}
        </p>
      </td>

      <td className="px-4 py-4">
        <StatusBadge row={row} />
      </td>

      {currentUser.type !== "agent" && (
        <td className="px-4 py-4 text-end">
          <UserActionsMenu row={row} tableAction={tableAction} />
        </td>
      )}
    </tr>
  );
};

export default UserRow;
